import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Chip,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

interface TerraformResourcePickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (resourceType: string) => void;
  resourceTypes: string[];
}

function groupByPrefix(types: string[]): Record<string, string[]> {
  const groups: Record<string, string[]> = {};
  for (const t of types) {
    // e.g. "rangeos_vmspecification" → prefix "vm"
    const parts = t.split('_');
    // Skip the provider prefix (first part), group by second part
    const prefix = parts.length > 1 ? parts[1] : 'other';
    if (!groups[prefix]) groups[prefix] = [];
    groups[prefix].push(t);
  }
  return groups;
}

export const TerraformResourcePicker: React.FC<TerraformResourcePickerProps> = ({
  open,
  onClose,
  onSelect,
  resourceTypes,
}) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return resourceTypes;
    const lower = search.toLowerCase();
    return resourceTypes.filter(t => t.toLowerCase().includes(lower));
  }, [resourceTypes, search]);

  const grouped = useMemo(() => groupByPrefix(filtered), [filtered]);
  const sortedGroups = useMemo(
    () => Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)),
    [grouped],
  );

  const handleSelect = (type: string) => {
    onSelect(type);
    onClose();
    setSearch('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Resource</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          placeholder="Search resource types..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoFocus
          sx={{ mb: 2, mt: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {filtered.length} resource type{filtered.length !== 1 ? 's' : ''}
        </Typography>
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {sortedGroups.map(([prefix, types]) => (
            <Box key={prefix} sx={{ mb: 1 }}>
              <Typography variant="overline" color="text.secondary" sx={{ px: 2 }}>
                {prefix} ({types.length})
              </Typography>
              <List dense disablePadding>
                {types.map(type => (
                  <ListItem key={type} disablePadding>
                    <ListItemButton onClick={() => handleSelect(type)}>
                      <ListItemText
                        primary={type}
                        primaryTypographyProps={{ variant: 'body2', fontFamily: 'monospace' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
          {filtered.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No matching resource types
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
